import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/session';
import { date } from '../lib/fmt';
import { Button, Card, Field, Input, Note, PageHead, Tag, Tile, Tiles } from '../components/ui';
import { Loading, TilesSkeleton, CardSkeleton, NoteSkeleton, Bone } from '../components/skeleton';

interface Preview {
  total: number; from: string; to: string; bytes: number;
  counts: { sales: number; receipts: number; payments: number; contra: number; credit_notes: number };
  ledgers: string[];
}

/**
 * The preview: the voucher counts, the download card, then the standing note.
 *
 * The head and the date fields are not waiting on anything, so they stay put
 * and only the result below them is drawn as a skeleton.
 */
function PreviewSkeleton() {
  return (
    <Loading label="Building the export">
      <TilesSkeleton />
      <CardSkeleton>
        <NoteSkeleton lines={2} />
        <Bone w={180} h={11} className="mt-3.5" />
      </CardSkeleton>
      <NoteSkeleton lines={2} />
    </Loading>
  );
}

export default function TallyExport() {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [to, setTo] = useState(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10));
  const [range, setRange] = useState({ from, to });

  const feed = useAsync<Preview>(
    () => api.get('/api/tally/preview', { from: range.from, to: range.to }), [range]);

  return (
    <div>
      <PageHead eyebrow="Compliance" title="Tally export"
        dek="Books is where a transaction is created — with its photograph, its approver and its project. Tally is where it is filed. This renders a period into Tally's import format so the accountant loads one file instead of retyping a month." />

      <div className="flex flex-wrap items-end gap-2.5 mb-5">
        <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button onClick={() => setRange({ from, to })}>Preview</Button>
      </div>

      {feed.loading && <PreviewSkeleton />}
      {feed.error && <Note title="Could not build the export" tone="critical">{feed.error}</Note>}

      {feed.data && (
        <>
          <Tiles>
            <Tile hero label="Vouchers" value={String(feed.data.total)}
                  sub={<span className="text-muted">{date(feed.data.from)} – {date(feed.data.to)}</span>} />
            <Tile label="Sales" value={String(feed.data.counts.sales + feed.data.counts.credit_notes)}
                  sub={<span className="text-muted">
                    {feed.data.counts.credit_notes} credit note{feed.data.counts.credit_notes === 1 ? '' : 's'}</span>} />
            <Tile label="Payments & receipts"
                  value={String(feed.data.counts.payments + feed.data.counts.receipts)}
                  sub={<span className="text-muted">
                    {feed.data.counts.receipts} in, {feed.data.counts.payments} out</span>} />
            <Tile label="Contra" value={String(feed.data.counts.contra)}
                  sub={<span className="text-muted">transfers between accounts</span>} />
          </Tiles>

          <Card title="Download" sub="In Tally: Gateway of Tally → Import → Vouchers, then pick this file."
                actions={
                  <Button variant="primary"
                    onClick={() => api.download('/api/tally/export', { from: range.from, to: range.to })}>
                    Download {(feed.data.bytes / 1024).toFixed(0)} KB XML
                  </Button>
                }>
            <Note title="Before you import">
              Every ledger below has to already exist in Tally, or the import fails on the first
              voucher that needs it. <b>{feed.data.ledgers.length} ledgers</b> are referenced.
            </Note>
            <details className="mt-3.5">
              <summary className="cursor-pointer text-[13px] text-ink-2">Show the ledger list</summary>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {feed.data.ledgers.map((l) => <Tag key={l}>{l}</Tag>)}
              </div>
            </details>
          </Card>

          <Note title="One rule for the two systems to agree">
            Anything entered straight into Tally stays invisible here. Enter it in Books first and
            export — otherwise the two sets of numbers drift apart and neither can be trusted.
          </Note>
        </>
      )}
    </div>
  );
}
